import { Link } from "react-router-dom";
import { ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useStore } from "@/context/StoreContext";
import { imgSrc } from "@/lib/api";
import { grams, tl } from "@/lib/format";
import { QtyControl } from "./QtyControl";
import { FreeShippingBar } from "./FreeShippingBar";

export const CartLine = ({ item, idx, prefix = "cart" }) => {
  const { setQty, removeItem, quote } = useStore();
  const stale = quote?.lines.some((l) => l.product_id === item.product_id && l.grams === item.grams && l.missing_options?.length && l.variant_label === Object.values(item.options || {}).filter(Boolean).join(", "));
  return (
    <div data-testid={`${prefix}-item-${idx}`} className="flex gap-4 py-4">
      <img src={imgSrc(item.image, 160)} alt={item.name} className="h-20 w-20 flex-none rounded-md object-cover" loading="lazy" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/urun/${item.slug}`} className="block truncate font-medium hover:text-accent">{item.name}</Link>
            {item.grams && <span className="text-xs text-muted-foreground">{grams(item.grams)} paket</span>}
            {item.options && Object.keys(item.options).length > 0 && (
              <span data-testid={`${prefix}-item-${idx}-options`} className="block text-xs text-muted-foreground">{Object.entries(item.options).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
            )}
            {stale && (
              <Link to={`/urun/${item.slug}`} data-testid={`${prefix}-item-${idx}-reselect`} className="block text-xs text-accent underline">Seçim gerekli – ürün sayfasından yeniden ekleyin</Link>
            )}
          </div>
          <button data-testid={`${prefix}-item-${idx}-remove`} aria-label="Kaldır" onClick={() => removeItem(item.key)} className="text-muted-foreground hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <QtyControl size="sm" value={item.qty} onChange={(q) => setQty(item.key, q)} testId={`${prefix}-item-${idx}-qty`} />
          <span data-testid={`${prefix}-item-${idx}-total`} className="font-medium tabular-nums">{tl(item.unit_price * item.qty)}</span>
        </div>
      </div>
    </div>
  );
};

export const MiniCart = () => {
  const { items, quote, localSubtotal, cartOpen, setCartOpen } = useStore();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="mini-cart" className="flex w-full flex-col bg-background p-0 sm:max-w-md">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle className="font-display text-2xl">Sepetim</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground" data-testid="mini-cart-empty">Sepetiniz şu anda boş.</p>
            <Button data-testid="mini-cart-continue" variant="outline" className="rounded-full" onClick={() => setCartOpen(false)} asChild>
              <Link to="/urunler">Alışverişe Başla</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4"><FreeShippingBar testId="mini-cart-shipping-bar" /></div>
            <div className="flex-1 divide-y overflow-y-auto px-6">
              {items.map((it, i) => <CartLine key={it.key} item={it} idx={i} prefix="mini-cart" />)}
            </div>
            <div className="space-y-3 border-t bg-card px-6 py-5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Ara Toplam</span>
                <span data-testid="mini-cart-subtotal" className="tabular-nums">{tl(quote?.subtotal ?? localSubtotal)}</span>
              </div>
              {quote?.discount_total > 0 && (
                <div className="flex justify-between text-sm text-accent">
                  <span>İndirimler</span><span className="tabular-nums">-{tl(quote.discount_total)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Kargo</span>
                <span data-testid="mini-cart-shipping" className="tabular-nums">{quote ? (quote.shipping_fee ? tl(quote.shipping_fee) : "Ücretsiz") : "-"}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold">
                <span>Toplam</span>
                <span data-testid="mini-cart-total" className="tabular-nums">{tl(quote?.total ?? localSubtotal)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Fiyatlara KDV dahildir.</p>
              <div className="grid grid-cols-2 gap-3">
                <Button data-testid="mini-cart-view-cart" variant="outline" className="rounded-full" asChild onClick={() => setCartOpen(false)}>
                  <Link to="/sepet">Sepete Git</Link>
                </Button>
                <Button data-testid="mini-cart-checkout" className="btn-press rounded-full bg-accent text-white hover:bg-accent/90" asChild onClick={() => setCartOpen(false)}>
                  <Link to="/odeme">Ödemeye Geç</Link>
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
